import { useNavigate, useSearch } from '@tanstack/react-router';

import { LoginForm } from './LoginForm';
import { PublicPageShell } from './PublicPageShell';
import { DEFAULT_POST_LOGIN_PATH, sanitizeRedirect } from '@/lib/auth-redirect';

export interface LoginPageProps {
  /** Title to display on the login page */
  title?: string;
  /** Description to display below the title */
  description?: string;
  /** Fallback URL to redirect to after successful login, when no deeplink was captured */
  redirectUrl?: string;
  /** Called when "Forgot Password" link is clicked; defaults to the /forgot page */
  onForgotPassword?: () => void;
}

export function LoginPage({
  title = 'Sign in',
  description = 'Enter your username and password to continue',
  redirectUrl = DEFAULT_POST_LOGIN_PATH,
  onForgotPassword,
}: LoginPageProps) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: unknown };
  // The deeplink the auth guard captured wins over the fallback landing page
  const target = sanitizeRedirect(search.redirect) ?? redirectUrl;

  const handleForgotPassword =
    onForgotPassword ?? (() => navigate({ to: '/forgot' }));

  const handleSuccess = () => {
    // `href` (not `to`) because the captured deeplink is a fully built path,
    // including stack separators and panel-state search params
    navigate({ href: target, replace: true });
  };

  return (
    <PublicPageShell title={title} description={description}>
      <LoginForm
        isModal={false}
        onSuccess={handleSuccess}
        onForgotPassword={handleForgotPassword}
      />
    </PublicPageShell>
  );
}

export default LoginPage;
